import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from "framer-motion";

import s01 from './sections/01-general.md?raw';
// ... (imports remain same)
import s02 from './sections/02-eligibility.md?raw';
import s03 from './sections/03-services.md?raw';
import s04 from './sections/04-fees.md?raw';
import s05 from './sections/05-representations.md?raw';
import s06 from './sections/06-risks.md?raw';
import s07 from './sections/07-prohibited-activities.md?raw';
import s08 from './sections/08-disclaimer.md?raw';
import s09 from './sections/09-limitation-of-liability.md?raw';
import s10 from './sections/10-intellectual-property.md?raw';
import s11 from './sections/11-third-party-services.md?raw';
import s12 from './sections/12-links.md?raw';
import s13 from './sections/13-governing-law.md?raw';
import s14 from './sections/14-miscellaneous.md?raw';

const ALL_SECTIONS = [s01, s02, s03, s04, s05, s06, s07, s08, s09, s10, s11, s12, s13, s14];

/** Minimal markdown → React renderer (handles the subset used in the terms docs) */
function renderMarkdown(raw: string): React.ReactNode[] {
    const lines = raw.split('\n');
    const nodes: React.ReactNode[] = [];
    let key = 0;

    const bold = (text: string): React.ReactNode[] => {
        const parts = text.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((p, i) =>
            p.startsWith('**') && p.endsWith('**')
                ? <strong key={i}>{p.slice(2, -2)}</strong>
                : p
        );
    };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        if (line.startsWith('# ')) {
            nodes.push(<h1 key={key++} className="text-2xl font-bold mt-8 mb-4 text-foreground">{line.slice(2)}</h1>);
        } else if (line.startsWith('## ')) {
            nodes.push(<h2 key={key++} className="text-lg font-semibold mt-6 mb-2 text-foreground">{line.slice(3)}</h2>);
        } else if (/^\d+\. /.test(line)) {
            const content = line.replace(/^\d+\. /, '');
            nodes.push(
                <li key={key++} className="mb-2 leading-relaxed text-muted-foreground">
                    {bold(content)}
                </li>
            );
        } else if (line.trim() === '') {
            nodes.push(<div key={key++} className="mb-2" />);
        } else {
            nodes.push(
                <p key={key++} className="mb-3 leading-relaxed text-muted-foreground">
                    {bold(line)}
                </p>
            );
        }
    }

    return nodes;
}

const TermsPage: React.FC = () => {
    return (
        <div className="min-h-screen flex flex-col text-foreground">
            {/* Background particles */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                {[...Array(30)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1 h-1 rounded-full bg-primary/40"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                        animate={{
                            y: [-20, -120],
                            x: [0, (Math.random() - 0.5) * 50],
                            opacity: [0, 0.8, 0],
                            scale: [0, 1, 0],
                        }}
                        transition={{
                            duration: 5 + Math.random() * 5,
                            repeat: Infinity,
                            delay: Math.random() * 5,
                            ease: "easeOut",
                        }}
                    />
                ))}
            </div>

            <div className="max-w-3xl mx-auto px-6 py-12 relative z-10">
                {/* Header */}
                <div className="mb-10">
                    <Link
                        to="/"
                        className="text-sm hover:text-foreground transition-colors mb-6 inline-block"
                        
                    >
                        ← Back to Portal
                    </Link>
                    <h1 className="text-3xl font-bold text-foreground mt-4">Privacy Portal Terms of Use</h1>
                </div>

                {/* Sections */}
                <div className="space-y-2">
                    {ALL_SECTIONS.map((section, idx) => (
                        <section key={idx}>
                            <ol className="list-decimal list-inside space-y-1">
                                {renderMarkdown(section)}
                            </ol>
                            {idx < ALL_SECTIONS.length - 1 && (
                                <hr className="border-border my-8" />
                            )}
                        </section>
                    ))}
                </div>

                {/* Footer nav */}
                <div className="mt-16 pt-6 border-t border-border text-center">
                    <Link
                        to="/"
                        className="text-sm hover:text-foreground transition-colors"
                        
                    >
                        ← Back to Portal
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default TermsPage;
